import { createContext } from 'react';
import type { SelectedItem, SelectedItemWithColor } from '@/types/content';

export interface ListQueryStatus {
  isListPending: boolean;
  isListEmpty: boolean;
}

/** Before the table reports, treat the list as pending so the chart spinner shows on first paint. */
export const INITIAL_LIST_STATUS: ListQueryStatus = { isListPending: true, isListEmpty: false };

interface SelectableMetricsContextValue {
  /** Canonical shared selection — the table's checkboxes and the chart's lines read this. */
  selectedItems: SelectedItemWithColor[];
  /** Apply a user-driven selection change (checkbox toggle, select-all, deselect-all). */
  select: (items: SelectedItem[]) => void;
  /**
   * Fill in a default selection (e.g. top N on first load), but only while the user hasn't
   * touched selection yet and nothing is currently selected. Passing an empty list is a no-op.
   */
  seedSelection: (items: SelectedItem[]) => void;
  /**
   * Clear selection + colours, re-arm auto-seed, and restore list-pending default until the table
   * reports the new query. For filter/dataset changes.
   */
  reset: () => void;
  /**
   * True after the user changes selection (including clearing it). False on first load and after
   * reset. Distinguishes "never seeded" from "cleared on purpose".
   */
  hasTouched: boolean;
  /** True on first list load; false during pagination (`keepPreviousData` placeholder). */
  isListPending: boolean;
  /** True once the list query has settled with no chart-seedable rows. */
  isListEmpty: boolean;
  /** Table reports list query status after each update (via layout effect, not render-phase setState). */
  updateListStatus: (status: ListQueryStatus) => void;
}

export const SelectableMetricsContext = createContext<SelectableMetricsContextValue>({
  selectedItems: [],
  select: () => {},
  seedSelection: () => {},
  reset: () => {},
  hasTouched: false,
  ...INITIAL_LIST_STATUS,
  updateListStatus: () => {},
});
